/**
 * Tenant theme presets.
 *
 * A preset is a whole visual identity layered on top of the CSS custom
 * properties the Sass layer already reads (see sass/utilities/_variables.scss,
 * where every `$blue-` and `$color-` variable is a `var(--color-…)`).
 * `default` is the look the portal has today and is what every tenant without
 * a preset gets — it loads nothing and sets no attribute, so its cascade is
 * untouched.
 *
 * `aperio` matches https://aperiomobility.com so a visitor handed over from the
 * marketing site stays on what reads as one website. Its stylesheet lives in
 * `public/aperio/theme.css` rather than in the Sass bundle precisely so that it
 * cannot reach a default-preset tenant: it is a separate file, injected here,
 * and every rule in it is scoped to `html[data-theme-preset="aperio"]`. The
 * Instrument fonts are self-hosted under `public/aperio/fonts/` (same origin,
 * no request to Google), mirroring what the marketing site ships.
 */

export type ThemePreset = 'default' | 'aperio';

const PRESETS: ThemePreset[] = ['default', 'aperio'];

/** Stylesheets a preset needs, in cascade order. Relative to PUBLIC_URL. */
const PRESET_STYLESHEETS: Record<ThemePreset, string[]> = {
  default: [],
  // consumer.css: the consumer booking skin's screens (client/src/consumer).
  aperio: ['aperio/fonts.css', 'aperio/theme.css', 'aperio/consumer.css'],
};

/** Give a slow or missing stylesheet a ceiling so boot never hangs on it. */
const STYLESHEET_TIMEOUT_MS = 4000;

/**
 * Anything unknown, empty or null is the default preset, which is what keeps
 * every existing tenant (Go Oil included) on today's rendering.
 */
export function normalizeThemePreset(value?: string | null): ThemePreset {
  const preset = (value || '').trim().toLowerCase();
  return (PRESETS as string[]).includes(preset)
    ? (preset as ThemePreset)
    : 'default';
}

function publicUrl(path: string) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/${path}`;
}

function presetLink(href: string): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>(
    `link[data-theme-preset-href="${href}"]`,
  );
}

function loadStylesheet(href: string): Promise<void> {
  return new Promise((resolve) => {
    // Already requested - by an earlier call, or by public/index.html, which
    // starts the preset's stylesheets on an Aperio host while the bundle is
    // still downloading (QA 2026-09-25 #14 / #52). Wait for it to arrive
    // rather than rendering against a sheet that is still in flight.
    const existing = presetLink(href);
    if (existing && (existing.sheet || existing.getAttribute('data-loaded'))) {
      resolve();
      return;
    }

    const link = existing || document.createElement('link');

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    link.addEventListener('load', () => {
      link.setAttribute('data-loaded', 'true');
      done();
    });
    // A 404 or a blocked request must not stop the app from rendering: the
    // preset simply does not apply and the portal falls back to the tenant's
    // own colours.
    link.addEventListener('error', done);
    window.setTimeout(done, STYLESHEET_TIMEOUT_MS);

    if (!existing) {
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-theme-preset-href', href);
      document.head.appendChild(link);
    }
  });
}

/**
 * True when one of a preset's stylesheets is in the document and applied.
 * Lets a component that must not paint unstyled (AperioLiveScene) render
 * straight away when public/index.html or an earlier visit already has it.
 */
export function isPresetStylesheetLoaded(path: string): boolean {
  const link = presetLink(publicUrl(path));
  return !!link && !!(link.sheet || link.getAttribute('data-loaded'));
}

/**
 * A URL for a file shipped under `public/` beside the preset's stylesheets.
 * Exported so a preset-only component can reach its own assets the same way
 * this file reaches the preset's CSS, PUBLIC_URL included.
 */
export function presetAssetUrl(path: string): string {
  return publicUrl(path);
}

/**
 * Loads one of a preset's stylesheets on demand, after boot. Same helper the
 * preset itself uses, so the de-duplication, the error fallback and the
 * timeout are the same: components/AperioLiveScene.tsx pulls in
 * `aperio/live-scene.css` only on the step that has the scene, rather than
 * making every Aperio page pay for it.
 */
export function loadPresetStylesheet(path: string): Promise<void> {
  return loadStylesheet(publicUrl(path));
}

/**
 * Loads one of a preset's scripts on demand, once per document. `public/` is
 * where the marketing site's own files are copied to byte-for-byte (see
 * scripts/check-aperio-assets.js), so they are loaded as the site loads them -
 * a plain <script src> - rather than being rewritten as modules and drifting.
 *
 * Resolves on load, and also on error or after the same ceiling a stylesheet
 * gets: a decorative scene that does not arrive must never leave a caller
 * awaiting it forever.
 */
export function loadPresetScript(path: string): Promise<void> {
  const src = publicUrl(path);
  return new Promise((resolve) => {
    const existing = document.querySelector(
      `script[data-theme-preset-src="${src}"]`,
    );
    if (existing) {
      if (existing.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => resolve());
      window.setTimeout(resolve, STYLESHEET_TIMEOUT_MS);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.setAttribute('data-theme-preset-src', src);

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    script.addEventListener('load', () => {
      script.setAttribute('data-loaded', 'true');
      done();
    });
    script.addEventListener('error', done);
    window.setTimeout(done, STYLESHEET_TIMEOUT_MS);

    document.head.appendChild(script);
  });
}

/**
 * Applies a preset to the document. Resolves once the preset's stylesheets are
 * in (or gave up), so the caller can render without a flash of the unthemed
 * portal. A `default` preset is a no-op.
 */
export async function applyThemePreset(preset: ThemePreset): Promise<void> {
  if (preset === 'default') return;

  const root = document.documentElement;
  root.setAttribute('data-theme-preset', preset);

  await Promise.all(
    PRESET_STYLESHEETS[preset].map((path) => loadStylesheet(publicUrl(path))),
  );
}
